import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function normalizePagedResponse(data) {
  if (Array.isArray(data)) {
    return { items: data, count: data.length };
  }
  const items = Array.isArray(data?.results) ? data.results : [];
  const count = typeof data?.count === "number" ? data.count : items.length;
  return { items, count };
}

export const fetchInventoryByStore = createAsyncThunk(
  "inventory/fetchByStore",
  async ({ storeId, page = 1, pageSize = 50, search = "", ordering = "-updated_at" } = {}) => {
    const qp = new URLSearchParams();
    qp.set("store", String(storeId));
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    qp.set("ordering", String(ordering || "-updated_at"));
    qp.set("page", String(page || 1));
    qp.set("page_size", String(pageSize || 50));
    const path = `/api/product/inventory/${qp.toString() ? `?${qp.toString()}` : ""}`;
    const data = await apiFetch(path, { method: "GET" });
    return { storeId: String(storeId), ...normalizePagedResponse(data) };
  }
);

const slice = createSlice({
  name: "inventory",
  initialState: {
    byStoreId: {}, // storeId -> { items, count, status, error, lastPage, lastPageSize, lastSearch }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchInventoryByStore.pending, (state, action) => {
        const sid = String(action.meta.arg.storeId);
        if (!state.byStoreId[sid]) {
          state.byStoreId[sid] = { items: [], count: 0, status: "idle", error: null };
        }
        state.byStoreId[sid].status = "loading";
        state.byStoreId[sid].error = null;
        state.byStoreId[sid].lastPage = action.meta.arg?.page || 1;
        state.byStoreId[sid].lastPageSize = action.meta.arg?.pageSize || 50;
        state.byStoreId[sid].lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchInventoryByStore.fulfilled, (state, action) => {
        const sid = String(action.payload.storeId);
        if (!state.byStoreId[sid]) {
          state.byStoreId[sid] = { items: [], count: 0, status: "idle", error: null };
        }
        state.byStoreId[sid].status = "succeeded";
        state.byStoreId[sid].items = action.payload?.items || [];
        state.byStoreId[sid].count = action.payload?.count ?? state.byStoreId[sid].items.length;
      })
      .addCase(fetchInventoryByStore.rejected, (state, action) => {
        const sid = String(action.meta.arg.storeId);
        if (!state.byStoreId[sid]) {
          state.byStoreId[sid] = { items: [], count: 0, status: "idle", error: null };
        }
        state.byStoreId[sid].status = "failed";
        state.byStoreId[sid].error = action.error?.message || "Failed to load inventory";
      });
  },
});

export default slice.reducer;

export const selectInventoryByStore = (storeId) => (state) => {
  const sid = String(storeId);
  return state.inventory.byStoreId[sid]?.items || [];
};

export const selectInventoryCountByStore = (storeId) => (state) => {
  const sid = String(storeId);
  return Number(state.inventory.byStoreId[sid]?.count || 0);
};

export const selectInventoryStatusByStore = (storeId) => (state) => {
  const sid = String(storeId);
  return state.inventory.byStoreId[sid]?.status || "idle";
};

export const selectInventoryErrorByStore = (storeId) => (state) => {
  const sid = String(storeId);
  return state.inventory.byStoreId[sid]?.error || null;
};
